import { selectBody } from '../../../store/selectors'
import { selectAvApiHost } from 'globalSelectors'
import { createSelector } from 'reselect'
import { selectAllAssets } from 'providers/AssetLoader/store/selectors'
import { testProgrammeObject } from '../testProgrammeObject'
import { v4 as uuidv4 } from 'uuid';
import { mergeChanges, mergeEpisodeChanges, mergeEpisodeChangesOnSave, mergeEpisodeListChangesOnSave } from 'modules/data-entry/AudioVisual/dataTransforms'
import { isFilmByFormatId } from 'modules/Constants'
import { selectUserName } from '../../../../../providers/Authentication/store/selectors'

export const selectBodyDatEntryApp = (store) => selectBody(store).dataEntry
export const selectBodyDataEntryProgramme = (store) => selectBodyDatEntryApp(store).audioVisual


const episodeFetchRequest = (store, programmeId, episodeId, url) => ({ endpoint: `${url}/${programmeId}/Episodes/${episodeId}`, status: store.status })
const createRequest = (store, programmeId, url) => ({ endpoint: `${url}/${programmeId}`, status: store.status, method: 'PUT' })
const programmeUpdateRequest = (store, programmeId, url, draft, original, episodeDraft, episodeOriginal, aggregateVersion, userName) => ({ endpoint: `${url}/${programmeId}`, status: store.updateStatus, body: draft, original: original, episodeDraft: episodeDraft, episodeOriginal: episodeOriginal, aggregateVersion: aggregateVersion, userName: userName })
const episodeUpdateRequest = (store, programmeId, episodeId, status, url, episodeDraft, episodeOriginal, aggregateVersion, userName) => ({ endpoint: `${url}/${programmeId}/Episodes/${episodeId}`, status: status, episodeDraft: episodeDraft, episodeOriginal: episodeOriginal, aggregateVersion: aggregateVersion, userName: userName })
const episodeListUpdateRequest = (store, programmeId, status, url, episodeListDraft, aggregateVersion) => ({ endpoint: `${url}/${programmeId}/EpisodesList`, status: status, episodeListDraft: episodeListDraft, aggregateVersion: aggregateVersion})

/*AudioVisual */

export const selectProgrammeUrl = createSelector(
    [selectAvApiHost],
    host => `${host}/Programme`
)

export const selectProgrammeGuid = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.guid
)

//TODO: Test selector. When creating a programme I believe a new GUID needs to be generated and added to the store
export const selectFakeProgrammeGuid = createSelector(
    [],
    () => uuidv4()
)

export const selectIsNewProgramme = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.isNewProgramme
)

export const selectIsNewEpisode = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.isNewEpisode
)

export const selectUpdateStatus = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.updateStatus
)

export const selectUpdateErrorDetailMessage = createSelector(
    [selectUpdateStatus],
    (status) => status.error ? status.error.Message : null
)

export const selectAudioVisualResultOriginal = createSelector(
    [selectBodyDataEntryProgramme, selectProgrammeGuid],
    (av, guid) => av[guid]?.original
)

export const selectAudioVisualResultOriginalAggregateVersion = createSelector(
    [selectBodyDataEntryProgramme, selectProgrammeGuid],
    (av, guid) => av[guid]?.original.version
)

export const selectAudioVisualResultDraft = createSelector(
    [selectBodyDataEntryProgramme, selectProgrammeGuid],
    (av, guid) => av[guid]?.draft
)

export const selectAudioVisualResultPresentation = createSelector(
    [selectAudioVisualResultOriginal, selectAudioVisualResultDraft],
    mergeChanges
)
export const selectAudioVisualResultPresentationTitles = createSelector(
    [selectAudioVisualResultPresentation],
    (presentation) => presentation?.titles
)

export const selectProgrammeCreateRequest = createSelector(
    [selectBodyDataEntryProgramme, selectFakeProgrammeGuid, selectProgrammeUrl, selectAudioVisualResultDraft, selectAudioVisualResultOriginal],
    createRequest
)
export const selectAudioVisualRefresh = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.refresh
)

//TODO: need to replace this with a selector that pulls from the actual programme data in the store
//Need to coerce this data into the format accepted by the API
export const selectProgrammeCreateBody = createSelector(
    [],
    testProgrammeObject
)

export const selectEpisodeResultOriginal = createSelector(
    [selectBodyDataEntryProgramme, selectProgrammeGuid],
    (av, programmeId) => av[programmeId]?.episode?.original
)

export const selectEpisodeResultDraft = createSelector(
    [selectBodyDataEntryProgramme, selectProgrammeGuid],
    (av, programmeId) => av[programmeId]?.episode?.draft
)

export const selectEpisodeResultStatus = createSelector(
    [selectBodyDataEntryProgramme, selectProgrammeGuid],
    (av, programmeId) => av[programmeId]?.episode?.status
)
export const selectEpisodeResultUpdateStatus = createSelector(
    [selectEpisodeResultStatus],
    (status) => ({
        updateStatus: status?.updateStatus,
        error: status?.error || null,
    })
)

export const selectProgrammeUpdateRequest = createSelector(
    [selectBodyDataEntryProgramme, selectProgrammeGuid, selectProgrammeUrl, selectAudioVisualResultDraft, selectAudioVisualResultOriginal, selectEpisodeResultDraft, selectEpisodeResultOriginal, selectAudioVisualResultOriginalAggregateVersion, selectUserName],
    programmeUpdateRequest
)

export const selectProgrammeFormats = createSelector(
    [selectAllAssets],
    (assets) => ({ programmeTypes: assets.programmeTypes, programmeFormatTypes: assets.programmeFormatTypes })
)

export const selectEpisodeGuid = createSelector(
    [selectBodyDataEntryProgramme, selectProgrammeGuid],
    (av, programmeId) => av[programmeId]?.episode?.guid ?? null
)

export const selectEpisodeResultPresentation = createSelector(
    [selectEpisodeResultOriginal, selectEpisodeResultDraft],
    mergeEpisodeChanges
)
export const selectEpisodetPresentationEpisodeTitles = createSelector(
    [selectEpisodeResultPresentation],
    (presentation) => presentation?.titles
)
export const selectEpisodeFetchRequest = createSelector(
    [selectBodyDataEntryProgramme, selectProgrammeGuid, selectEpisodeGuid, selectProgrammeUrl],
    episodeFetchRequest
)

export const selectAudioVisualResultHasChanges = createSelector(
    [selectAudioVisualResultDraft, selectEpisodeResultDraft],
    (draft, episodeDraft) => (draft != null && Object.keys(draft).length > 0) || (episodeDraft != null && Object.keys(episodeDraft).length > 0)
)

export const selectProgrammeResultHasChanges = createSelector(
    [selectAudioVisualResultDraft, selectEpisodeResultDraft, selectBodyDataEntryProgramme, selectProgrammeGuid],
    (draft, episodeDraft, av, programmeId) => {
        const formatId = av[programmeId]?.original?.formatId
        const hasDraftChanges = draft && Object.keys(draft).length > 0
        const hasEpisodeDraftChanges = episodeDraft && Object.keys(episodeDraft).length > 0

        return isFilmByFormatId(formatId) 
            ? hasDraftChanges || hasEpisodeDraftChanges 
            : hasDraftChanges && !draft.episodes
    }
)

export const selectAudioVisualEpisodeListHasChanges = createSelector(
    [selectAudioVisualResultDraft],
    (draft) => draft != null && draft.episodes != null && Object.keys(draft.episodes).length > 0
)

export const selectAudioVisualEpisodeListFromDraft = createSelector(
    [selectAudioVisualResultDraft],
    (draft) => draft?.episodes
)

export const selectEpisodeResultHasChanges = createSelector(
    [selectEpisodeResultDraft],
    (episodeDraft) => (episodeDraft != null && Object.keys(episodeDraft).length > 0)
)

export const selectEpisodeDraftHasDeletedTransmissions = createSelector(
    [selectEpisodeResultDraft],
    (episodeDraft) => {
        if (!episodeDraft || !episodeDraft.transmissions) {
            return false;
        }
        return Object.values(episodeDraft.transmissions).some(transmission => transmission.isDeleted);
    }
)

export const selectEpisodeDraftHasTransmissions = createSelector(
    [selectEpisodeResultDraft],
    (episodeDraft) => {
        if (!episodeDraft || !episodeDraft.transmissions) {
            return false
        }
        return Object.keys(episodeDraft.transmissions).length > 0
    }
)

export const selectEpisodeDraftHasDeletedContributions = createSelector(
    [selectEpisodeResultDraft],
    (episodeDraft) => {
        if (!episodeDraft || !episodeDraft.contributions) {
            return false;
        }
        return Object.values(episodeDraft.contributions).some(contribution => contribution.isDeleted);
    }
)

export const selectEpisodeDraftHasContributions = createSelector(
    [selectEpisodeResultDraft],
    (episodeDraft) => {
        if (!episodeDraft || !episodeDraft.contributions) {
            return false
        }
        return Object.keys(episodeDraft.contributions).length > 0
    }
)

export const selectEpisodeListResultUpdateStatus = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.updateEpisodeListStatus
)

export const selectEpisodeUpdateRequest = createSelector(
    [selectBodyDataEntryProgramme, selectProgrammeGuid, selectEpisodeGuid, selectEpisodeResultUpdateStatus, selectProgrammeUrl, selectEpisodeResultDraft, selectEpisodeResultOriginal, selectAudioVisualResultOriginalAggregateVersion, selectUserName],
    episodeUpdateRequest
)

export const selectEpisodeListUpdateRequest = createSelector(
    [selectBodyDataEntryProgramme, selectProgrammeGuid, selectEpisodeListResultUpdateStatus, selectProgrammeUrl, selectAudioVisualEpisodeListFromDraft, selectAudioVisualResultOriginalAggregateVersion],
    episodeListUpdateRequest
)

//Episode Update Status

export const selectEpisodeUpdateErrorDetailMessage = createSelector(
    [selectEpisodeResultUpdateStatus],
    (updateStatus) => updateStatus?.error ? updateStatus.error.Message : null
)

export const selectShowDataUpdate = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.showDataUpdate
)

export const selectShowDataCreate = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.showDataCreate
)

export const selectDataUpdateKey = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.dataUpdateKey
)

export const selectShowEpisodeDataUpdate = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.showEpisodeDataUpdate
)

export const selectShowEpisodeDataCreate = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.showEpisodeDataCreate
)

export const selectEpisodeDataUpdateKey = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.episodeDataUpdateKey
)

export const selectAreEpisodeTitlesInvalid = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.areEpisodeTitlesInvalid
)

export const selectAreEpisodeLanguagesInvalid = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.areEpisodeLanguagesInvalid
)

export const selectEpisodeTitles = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.episodeTitles
)


export const selectAreProgrammeTitlesInvalid = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.areProgrammeTitlesInvalid
)

export const selectIsPrimaryTitleInvalid = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.isPrimaryTitleInvalid
)

export const selectAreProgrammeLanguagesInvalid = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.areProgrammeLanguagesInvalid
)

export const selectProgrammeTitles = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.programmeTitles
)

export const selectPrimaryTitle = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.programmePrimaryTitle
)

export const selectEpisodeListRowInEditCount = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.episodeListEditRowCount
)

export const selectContributionRowInEditCount = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.contributionEditRowCount
)

export const selectIsContributionRowEditing = createSelector(
    [selectContributionRowInEditCount],
    (count) => count > 0
)

export const selectAudiovisualSort = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.sort
)

export const selectAudiovisualFilter = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.filter
)

//On save and return back to list, get the changes saved from episode original and merge it to episodelist in Programme
export const selectMergeChangeEpisodeDetailToList = createSelector(
    [selectAudioVisualResultOriginal, selectEpisodeResultPresentation],
    mergeEpisodeChangesOnSave
)

export const selectMergeChangeEpisodeListToDetail = createSelector(
    [selectAudioVisualResultPresentation, selectEpisodeResultOriginal],
    mergeEpisodeListChangesOnSave
)

export const selectIsSaveAndExitEnabled = createSelector(
    [selectBodyDataEntryProgramme],
    (av) => av.isEpisodeSaveAndExitClicked === true
)

export const selectEpisodeListCurrentPage = createSelector([selectBodyDataEntryProgramme], (av) => av.episodeListCurrentPage)
export const selectEpisodeListFirst = createSelector([selectBodyDataEntryProgramme], (av) => av.episodeListFirst)
export const selectEpisodeListRows = createSelector([selectBodyDataEntryProgramme], (av) => av.episodeListRows)
export const selectEpisodeListTotalPages = createSelector([selectBodyDataEntryProgramme], (av) => av.episodeListTotalPages)
export const selectEpisodeListFetchStatus = createSelector([selectBodyDataEntryProgramme], (av) => av.episodeListFetchStatus)
export const selectEpisodeListPageData = createSelector([selectBodyDataEntryProgramme], (av) => av.episodeListPageData)

export const selectMergedEpisodeListData = createSelector(
    [selectAudioVisualResultOriginal, selectAudioVisualEpisodeListFromDraft],
    (programmeOriginal, draftEpisodes) => {
        if (!programmeOriginal?.episodes) return programmeOriginal

        // Convert episodes to array (stored as object keyed by episodeId for mergeChanges compatibility)
        const episodesArray = Array.isArray(programmeOriginal.episodes)
            ? programmeOriginal.episodes
            : Object.values(programmeOriginal.episodes)

        if (!draftEpisodes || Object.keys(draftEpisodes).length === 0) {
            // Return with episodes as array for LazyGrid
            return { ...programmeOriginal, episodes: episodesArray }
        }

        // Merge draft changes into episodes array
        const mergedEpisodes = episodesArray.map(episode => {
            const episodeId = episode.episodeId || episode.id
            const draftEpisode = draftEpisodes[episodeId]
            return draftEpisode ? { ...episode, ...draftEpisode } : episode
        })

        return { ...programmeOriginal, episodes: mergedEpisodes }
    }
)

export const selectEpisodesByProgrammeFetchRequest = createSelector(
    [selectBodyDataEntryProgramme, selectProgrammeGuid, selectEpisodeListCurrentPage, selectEpisodeListRows, selectAudiovisualFilter, selectAudiovisualSort, selectProgrammeUrl],
    (av, programmeId, page, count, filter, sort, url) => ({
        programmeId,
        page,
        count,
        filter,
        sort,
        url,
        status: av.episodeListFetchStatus,
        refresh: av.refresh
    })
)





